import { Link } from 'react-router'
import { Container } from '@/components/common'
import { APP } from '@/constants'
import { ROUTES } from '@/constants/routes'
import { SocialLinks } from '@/components/ui/SocialLinks'
import logo from '@/assets/logo.jpg'

const footerColumns = [
  {
    heading: 'Services',
    links: [
      { label: 'Consultations', path: ROUTES.SERVICES_CONSULTATIONS },
      { label: 'Health Scans', path: ROUTES.SERVICES_SCANS },
      { label: 'Courses & Training', path: ROUTES.SERVICES_COURSES },
      { label: '3 Month Programme', path: ROUTES.WORK_WITH_ME },
    ],
  },
  {
    heading: 'Health',
    links: [
      { label: "Women's Health", path: ROUTES.WOMENS_HEALTH },
      { label: 'Chronic Health', path: ROUTES.CHRONIC_HEALTH },
      { label: 'Hormonal Balance', path: ROUTES.WOMENS_HEALTH },
      { label: 'Menopause Support', path: ROUTES.WOMENS_HEALTH },
    ],
  },
  {
    heading: 'Learn',
    links: [
      { label: 'Books', path: ROUTES.BOOKS },
      { label: 'Training', path: ROUTES.TRAINING },
      { label: 'Blog & Resources', path: ROUTES.BLOG },
      { label: 'Products', path: ROUTES.PRODUCTS },
    ],
  },
  {
    heading: 'Company',
    links: [
      { label: 'About Kandake', path: ROUTES.ABOUT },
      { label: 'Contact', path: ROUTES.CONTACT },
    ],
  },
]

export const Footer = () => {
  return (
    <footer className="relative overflow-hidden bg-green-950 text-white/70">
      {/* Gold shimmer rule */}
      <div className="h-px w-full bg-gradient-to-r from-transparent via-gold-400/50 to-transparent" />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -left-32 top-10 h-96 w-96 rounded-full bg-green-800/20 blur-3xl"
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -right-24 bottom-10 h-80 w-80 rounded-full bg-gold-400/10 blur-3xl"
      />

      {/* Main body */}
      <Container className="relative z-10 py-16 lg:py-20">
        <div className="grid grid-cols-1 gap-14 lg:grid-cols-12">
          {/* Brand column */}
          <div className="lg:col-span-4 space-y-6">
            <Link
              to={ROUTES.HOME}
              className="inline-flex items-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-500 rounded-xl"
            >
              <div className="flex flex-col items-center rounded-xl bg-white px-3 py-2">
                <img src={logo} alt="Instrumental Health" className="h-16 w-16 object-contain" />
                <span className="mt-1 text-[0.62rem] font-medium uppercase tracking-[0.16em] text-green-900">
                  Changing the Mindset
                </span>
              </div>
            </Link>

            <p className="text-sm text-white/75 leading-relaxed max-w-[22rem]">
              {APP.TAGLINE}. Herbal medicine, clinical consultations, and natural health education,
              rooted in tradition, grounded in science.
            </p>

            {/* Social links */}
            <SocialLinks />

            {/* Email link */}
            <a
              href={`mailto:${APP.CONTACT_EMAIL}`}
              className="inline-flex items-center gap-2 text-sm text-white/70 hover:text-white transition-colors"
            >
              <svg
                className="w-4 h-4 shrink-0"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={1.5}
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75"
                />
              </svg>
              {APP.CONTACT_EMAIL}
            </a>
          </div>

          {/* Nav columns */}
          <div className="lg:col-span-8 grid grid-cols-2 gap-10 sm:grid-cols-4">
            {footerColumns.map((col) => (
              <div key={col.heading}>
                <h3 className="mb-5 border-b border-white/10 pb-3 font-display text-lg text-gold-200">
                  {col.heading}
                </h3>
                <ul className="space-y-3">
                  {col.links.map((l) => (
                    <li key={l.label}>
                      <Link
                        to={l.path}
                        className="inline-block text-[0.95rem] text-white/80 transition-all hover:translate-x-1 hover:text-gold-200"
                      >
                        {l.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-10 flex flex-col gap-4 border-t border-white/15 pt-8 sm:flex-row sm:items-center sm:justify-between">
          <p className="max-w-md text-sm leading-6 text-green-100">
            Continue exploring herbal knowledge, community learning, and Kandake’s work.
          </p>
          <Link
            to={ROUTES.BLOG}
            className="inline-flex min-h-12 items-center text-sm font-medium text-base-50 underline underline-offset-8 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold-200"
          >
            Explore articles &amp; resources →
          </Link>
        </div>
      </Container>

      {/* Bottom bar */}
      <div className="border-t border-green-900/70">
        <Container className="relative z-10 flex flex-col sm:flex-row items-center justify-between gap-2 py-5 text-xs text-white/65">
          <p>
            © {new Date().getFullYear()} {APP.NAME}. All rights reserved.
          </p>
          <p>
            Crafted by{' '}
            <a
              href="https://www.zhuwafrica.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-white/70 hover:text-white/60 transition-colors"
            >
              {APP.BUILT_BY}
            </a>
          </p>
        </Container>
      </div>
    </footer>
  )
}
